import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Controller } from "react-hook-form";
import axiosInstance from "utilities/axios";
import { useReassignForm } from "hooks/useReassignForm";
import { AssignmentSelect } from "components/common/FilterControls";
import { ApplicationPreview, Type } from "types";

type ActiveAssignment = {
  _id: string;
  organization: { _id: string; name: string };
  application_type: string;
  assigned_at: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  application: ApplicationPreview | null;
  tab?: Type;
  approvalStatusType?: string;
};

const ReassignDialog: React.FC<Props> = ({ open, onClose, onSuccess, application, tab, approvalStatusType }) => {
  const { control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useReassignForm();
  const [assignments, setAssignments] = useState<ActiveAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setServerError(null);
    reset();
    setLoadingAssignments(true);
    axiosInstance
      .get("/assignments", { params: { is_active: "true", application_type: "cgs" } })
      .then((res) => {
        const list: ActiveAssignment[] = res.data;
        setAssignments(list);
        if (list.length === 1) {
          setValue("monitoring_assignment", list[0]._id);
        }
      })
      .catch(() => setServerError("მონიტორინგის პერიოდების ჩატვირთვა ვერ მოხერხდა"))
      .finally(() => setLoadingAssignments(false));
  }, [open, reset, setValue]);

  const onSubmit = async (values: { monitoring_assignment: string; comment: string }) => {
    if (!application) return;
    try {
      const body: Record<string, string> = {
        monitoring_assignment: values.monitoring_assignment,
        comment: values.comment,
      };
      const statusType = approvalStatusType ?? (tab === Type.approved ? "REMONITORING" : undefined);
      if (statusType) {
        body.approval_status_type = statusType;
      }
      await axiosInstance.put(`/monitoring/reassign-cgs/${application._id}`, body);
      onSuccess();
      onClose();
    } catch {
      setServerError("გადამისამართება ვერ მოხერხდა");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>განცხადების გადამისამართება</DialogTitle>
      <DialogContent>
        {loadingAssignments ? (
          <CircularProgress size={24} sx={{ mt: 2 }} />
        ) : (
          <>
            {assignments.length >= 1 && (
              <Controller
                name="monitoring_assignment"
                control={control}
                rules={{ required: "გადამისამართება სავალდებულოა" }}
                render={({ field }) => (
                  <div style={{ marginTop: 16 }}>
                    <AssignmentSelect
                      label="გადამისამართება"
                      value={field.value}
                      onChange={field.onChange}
                      assignments={assignments.map((a) => ({
                        _id: a._id,
                        organization: a.organization,
                        application_type: a.application_type,
                        assigned_at: a.assigned_at,
                        ended_at: null,
                        is_active: true,
                      }))}
                      error={!!errors.monitoring_assignment}
                    />
                    {errors.monitoring_assignment && (
                      <Typography variant="caption" color="error">
                        {errors.monitoring_assignment.message}
                      </Typography>
                    )}
                  </div>
                )}
              />
            )}
            {assignments.length === 0 && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                აქტიური გადამისამართება ვერ მოიძებნა
              </Typography>
            )}
            <Controller
              name="comment"
              control={control}
              rules={{ required: "კომენტარი სავალდებულოა" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="კომენტარი"
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mt: 2 }}
                  error={!!errors.comment}
                  helperText={errors.comment?.message}
                />
              )}
            />
            {serverError && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                {serverError}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>გაუქმება</Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting || loadingAssignments || assignments.length === 0}
        >
          გადამისამართება
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReassignDialog;
